import { useDeferredValue, useMemo, useState } from 'react';
import { message, notification } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '~/app/auth/context/AuthContext';
import type { PolygonPerimeter } from '@evimo/types';
import {
  SiteService,
  type SiteLookup,
  type ZoneLookup,
} from '~/app/services/site/site.service';

export type ZoneFormValues = {
  name: string;
  siteId: string;
  latitude?: string;
  longitude?: string;
  perimeter?: PolygonPerimeter | null;
  isMain?: boolean;
};

function toNullableNumber(value?: string): number | null | undefined {
  if (value === undefined) {
    return undefined;
  }

  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return null;
  }

  return Number(normalizedValue);
}

export function useZoneManagement() {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';

  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [editingZone, setEditingZone] = useState<ZoneLookup | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const deferredSearchValue = useDeferredValue(
    searchValue.trim().toLowerCase(),
  );

  const sitesQuery = useQuery({
    queryKey: ['sites', organizationId],
    queryFn: () => SiteService.findAllByOrganizationId(organizationId),
    enabled: !!organizationId,
  });

  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);
  const activeSiteId = selectedSiteId || sites[0]?.id || '';

  const zonesQuery = useQuery({
    queryKey: ['zones', organizationId, activeSiteId],
    queryFn: () => SiteService.findZonesBySiteId(organizationId, activeSiteId),
    enabled: !!organizationId && !!activeSiteId,
  });

  const createMutation = useMutation({
    mutationFn: (values: ZoneFormValues) =>
      SiteService.createZone(organizationId, values.siteId, {
        name: values.name.trim(),
        latitude: toNullableNumber(values.latitude),
        longitude: toNullableNumber(values.longitude),
        perimeter: values.perimeter ?? null,
        isMain: values.isMain ?? false,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['zones'] });
      notification.success({
        message: 'Zone created successfully',
        description:
          'The zone is now available for monitoring and can be opened directly from the zones list.',
        placement: 'topRight',
        duration: 4.5,
      });
      setIsModalOpen(false);
      setEditingZone(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      zone,
      values,
    }: {
      zone: ZoneLookup;
      values: ZoneFormValues;
    }) =>
      SiteService.updateZoneById(organizationId, zone.siteId, zone.id, {
        name: values.name.trim(),
        siteId: values.siteId,
        latitude: toNullableNumber(values.latitude),
        longitude: toNullableNumber(values.longitude),
        perimeter: values.perimeter ?? null,
        isMain: values.isMain ?? false,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['zones'] });
      notification.success({
        message: 'Zone updated successfully',
        placement: 'topRight',
        duration: 4.5,
      });
      setIsModalOpen(false);
      setEditingZone(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (zone: ZoneLookup) =>
      SiteService.deleteZoneById(organizationId, zone.siteId, zone.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['zones'] });
      message.success('Zone deleted successfully.');
    },
  });

  const zones = useMemo(() => zonesQuery.data ?? [], [zonesQuery.data]);
  const filteredZones = useMemo(() => {
    if (!deferredSearchValue) {
      return zones;
    }

    return zones.filter((zone) =>
      zone.name.toLowerCase().includes(deferredSearchValue),
    );
  }, [deferredSearchValue, zones]);

  const siteNameById = useMemo(
    () =>
      new Map(
        sites.map((site) => [site.id, site.name] satisfies [string, string]),
      ),
    [sites],
  );

  const openCreateModal = (): void => {
    setEditingZone(null);
    setIsModalOpen(true);
  };

  const openEditModal = (zone: ZoneLookup): void => {
    setEditingZone(zone);
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
    setEditingZone(null);
  };

  const submitZone = async (values: ZoneFormValues): Promise<void> => {
    if (editingZone) {
      await updateMutation.mutateAsync({ zone: editingZone, values });
      return;
    }

    await createMutation.mutateAsync(values);
  };

  const deleteZone = async (zone: ZoneLookup): Promise<void> => {
    await deleteMutation.mutateAsync(zone);
  };

  return {
    activeSiteId,
    editingZone,
    error: sitesQuery.error ?? zonesQuery.error ?? null,
    filteredZones,
    isDeleting: deleteMutation.isPending,
    isFetching: sitesQuery.isFetching || zonesQuery.isFetching,
    isLoading: sitesQuery.isLoading || zonesQuery.isLoading,
    isModalOpen,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    searchValue,
    siteNameById,
    sites,
    closeModal,
    deleteZone,
    openCreateModal,
    openEditModal,
    refetch: zonesQuery.refetch,
    setSearchValue,
    setSelectedSiteId,
    submitZone,
  } satisfies {
    activeSiteId: string;
    editingZone: ZoneLookup | null;
    error: Error | null;
    filteredZones: ZoneLookup[];
    isDeleting: boolean;
    isFetching: boolean;
    isLoading: boolean;
    isModalOpen: boolean;
    isSubmitting: boolean;
    searchValue: string;
    siteNameById: Map<string, string>;
    sites: SiteLookup[];
    closeModal: () => void;
    deleteZone: (zone: ZoneLookup) => Promise<void>;
    openCreateModal: () => void;
    openEditModal: (zone: ZoneLookup) => void;
    refetch: () => Promise<unknown>;
    setSearchValue: (value: string) => void;
    setSelectedSiteId: (value: string) => void;
    submitZone: (values: ZoneFormValues) => Promise<void>;
  };
}
